// projectObjectivesAdapter — parse the `projectObjectives` reply at the
// boundary (`Quality.ObjectiveCoverage`). Pure. An objective without an id
// or a statement drops; a malformed part id, source or containment drops; at most
// `OBJECTIVES_MAX` objectives are kept whatever the reply holds (the
// server's ceiling, CL-20); a reply without a list is malformed.

export { OBJECTIVES_MAX, parseProjectObjectives };

import { Result } from "@bilda/utils";
import { type ExternalError, mkMalformed } from "./externalError.js";
import type { ProjectObjective, ProjectObjectives } from "./ports/project.js";
import { fields, list, num, str } from "./wireShape.js";

// The server's ceiling (CL-20); a reply with more is cut here.
const OBJECTIVES_MAX = 100;

const ids = (raw: unknown): readonly string[] =>
    list(raw).flatMap((v) => str(v) ?? []);

const sourceOf = (raw: unknown) => {
    const f = fields(raw);
    const id = str(f?.get("id"));
    return id === undefined ? [] : [{ id, title: str(f?.get("title")) ?? id }];
};

const withinOf = (raw: unknown): readonly [string, readonly string[]][] => {
    const f = fields(raw);
    const part = str(f?.get("part"));
    return part === undefined ? [] : [[part, ids(f?.get("containers"))]];
};

const objectiveOf = (raw: unknown): readonly ProjectObjective[] => {
    const f = fields(raw);
    const id = str(f?.get("id"));
    const statement = str(f?.get("statement"));
    return id === undefined || statement === undefined
        ? []
        : [
              {
                  id,
                  statement,
                  taught: ids(f?.get("taught")),
                  checked: ids(f?.get("checked")),
                  planned: ids(f?.get("planned")),
                  from: list(f?.get("from")).flatMap(sourceOf),
              },
          ];
};

const parseProjectObjectives = (
    raw: unknown,
): Result.Result<ProjectObjectives, ExternalError> => {
    const f = fields(raw);
    const objectives = f?.get("objectives");
    if (!Array.isArray(objectives))
        return Result.err(mkMalformed("projectObjectives returned no list"));
    const max = num(f?.get("max"));
    return Result.ok({
        objectives: objectives.slice(0, OBJECTIVES_MAX).flatMap(objectiveOf),
        within: new Map(list(f?.get("within")).flatMap(withinOf)),
        max:
            max === undefined
                ? OBJECTIVES_MAX
                : Math.min(Math.floor(max), OBJECTIVES_MAX),
    });
};
